import { isBrowser } from '@/utils/browser';
import { routes } from '@/webapi/routes';
import {
  AccountSortDirection,
  AccountSortOption,
  ConnectedStore,
} from '@/webapi/use-accounts-api';

export interface RecentStore {
  alias: string;
  domain: string;
}

export const RECENT_STORES_KEY = `loomi_recent_stores`;
export const MAX_RECENT_STORES = 8;
const CONNECTED_STORES_SESSION_KEY = `loomi_connected_stores`;
const CONNECTED_ACCOUNTS_SESSION_KEY = `loomi_connected_accounts`;

function readRecentStores(): RecentStore[] {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(RECENT_STORES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.warn(`Failed to parse recent stores from localStorage`, e);
    return [];
  }
}

function writeRecentStores(stores: RecentStore[]) {
  if (!isBrowser()) return;
  window.localStorage.setItem(RECENT_STORES_KEY, JSON.stringify(stores));
}

export function getRecentStores(): RecentStore[] {
  return readRecentStores();
}

export function addRecentStore(store: RecentStore): RecentStore[] {
  if (!store?.alias) return readRecentStores();

  const withoutCurrent = readRecentStores().filter(
    (s) => s.alias !== store.alias,
  );
  const updated = [store, ...withoutCurrent].slice(0, MAX_RECENT_STORES);

  writeRecentStores(updated);
  return updated;
}

export function fillWithConnectedStores(
  recent: RecentStore[],
  connected: RecentStore[],
): RecentStore[] {
  const known = new Set(recent.map((s) => s.alias));
  const missing = connected.filter((s) => s?.alias && !known.has(s.alias));
  return [...recent, ...missing].slice(0, MAX_RECENT_STORES);
}

export function toRecentStore(account: ConnectedStore): RecentStore {
  return {
    alias: account.storeAlias,
    domain:
      account.domain ||
      `${account.storeAlias?.replace(`_`, `-`)?.toLowerCase()}.myshopify.com`,
  };
}

export async function requestNewestAccounts(): Promise<ConnectedStore[]> {
  try {
    const resp = await fetch(routes.listAccounts(), {
      method: `POST`,
      credentials: `include`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify({
        page: 0,
        limit: MAX_RECENT_STORES * 2,
        sortOption: AccountSortOption.BY_CREATION_DATE,
        sortDirection: AccountSortDirection.DESC,
        searchText: ``,
      }),
    });
    const data = await resp.json();
    return data?.accounts || [];
  } catch (e) {
    console.warn(`Failed to fetch connected stores`, e);
    return [];
  }
}

export function readCachedNewestAccounts(): ConnectedStore[] | null {
  try {
    const raw = window.sessionStorage.getItem(CONNECTED_ACCOUNTS_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export async function refreshNewestAccounts(): Promise<ConnectedStore[]> {
  const accounts = await requestNewestAccounts();
  if (accounts.length > 0) {
    window.sessionStorage.setItem(
      CONNECTED_ACCOUNTS_SESSION_KEY,
      JSON.stringify(accounts),
    );
  }
  return accounts;
}

async function requestNewestConnectedStores(): Promise<RecentStore[]> {
  const accounts = await requestNewestAccounts();
  return accounts.map(toRecentStore);
}

function readCachedConnectedStores(): RecentStore[] | null {
  try {
    const raw = window.sessionStorage.getItem(CONNECTED_STORES_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export async function fetchNewestConnectedStores(): Promise<RecentStore[]> {
  if (!isBrowser()) return [];
  const cached = readCachedConnectedStores();
  if (cached) return cached;
  const stores = await requestNewestConnectedStores();
  if (stores.length > 0) {
    window.sessionStorage.setItem(
      CONNECTED_STORES_SESSION_KEY,
      JSON.stringify(stores),
    );
  }
  return stores;
}
